import styled from '@emotion/styled';
import { Edit, ExpandLess, ExpandMore } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import {
  Box,
  IconButton,
  Link as MuiLink,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  SvgIcon,
  SxProps,
} from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { FormTextField } from 'components/views/forms/form-text-field';
import format from 'date-fns/format';
import { useToggle } from 'hooks/use-toggle/use-toggle';
import { useSnackbar } from 'providers/snackbar';
import { FC, Fragment, FormEvent, ReactNode, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';
import { getTZ } from 'utils/date';
import { singleMessages } from 'utils/global-messages';
import messages from './messages';

type MetadataItemProps = {
  label: string;
  value?: ReactNode | string | null;
  actionIcon?: typeof SvgIcon;
  postText?: ReactNode;
  onClick?: () => void;
  to?: string;
  sx?: SxProps;
};

type MetadataItemEditableProps = {
  label: string;
  value: string | null;
  onSave: (newValue: string) => Promise<any>;
  customValidate?: RegExp;
  customValidateMessage?: string;
  validate?: (newValue: string) => string | undefined;
  optional?: boolean;
  sx?: SxProps;
};

type EditableTextProps = {
  label: string;
  value: string;
  onSave: (newValue: string) => Promise<any>;
  maxLength?: number;
  sx?: SxProps;
  children?: ReactNode;
};

export type MetadataItemCopyProps = MetadataItemProps & {
  valueToCopy?: string;
};

function MetadataItem({
  label,
  value,
  actionIcon: ActionIcon,
  onClick,
  to,
  postText,
  sx,
}: MetadataItemProps) {
  if (to) {
    return (
      <ListItemButton>
        <MuiLink
          component={Link}
          to={to}
          underline="none"
          color="inherit"
          sx={{ flex: '1', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        >
          <ListItemText primary={label} secondary={value} />
          <ChevronRightIcon color="action" />
        </MuiLink>
      </ListItemButton>
    );
  }
  if (onClick) {
    return (
      <ListItemButton onClick={onClick} sx={sx}>
        <ListItemText primary={label} secondary={value} />
        {!!ActionIcon && <ActionIcon color="action" />}
      </ListItemButton>
    );
  }
  return (
    <ListItem>
      <ListItemText primary={label} secondary={value} sx={sx} />
      {postText}
    </ListItem>
  );
}

const StyledStack = styled(Stack)({
  '&.expanded': {
    backgroundColor: '#eeeeee',
  },
  ':hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
  },
});

interface MetadataItemExpandableProps {
  text: ReactNode;
  postText?: ReactNode;
  values: [string, ReactNode | string][];
  initiallyOpen?: boolean;
  copyable?: boolean;
  sx?: SxProps;
  itemRenderer?: (key: string, value: ReactNode | string) => ReactNode;
}

export const MetadataItemExpandable: FC<MetadataItemExpandableProps> = ({
  text,
  postText,
  values,
  initiallyOpen = false,
  copyable = false,
  sx,
  itemRenderer,
}) => {
  const [expanded, toggle] = useToggle(initiallyOpen);

  return (
    <>
      <StyledStack direction="row" className={expanded ? 'expanded' : ''}>
        <ListItem>
          <ListItemText onClick={toggle} primary={text} />
          {values.length > 0 && (
            <IconButton onClick={toggle}>{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
          )}
          {postText}
        </ListItem>
      </StyledStack>
      {expanded &&
        values.map(([key, value]) => {
          if (itemRenderer) {
            return <Fragment key={key}>{itemRenderer(key, value)}</Fragment>;
          }

          return copyable && typeof value === 'string' ? (
            <MetadataItemCopy key={key} label={value} value={key} sx={sx} />
          ) : (
            <MetadataItem key={key} label={key} value={value} sx={sx} />
          );
        })}
    </>
  );
};

export const MetadataItemCopy: FC<MetadataItemCopyProps> = ({ label, value, valueToCopy, sx }) => {
  const { openSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const handleCopy = async () => {
    const toCopy = valueToCopy ? valueToCopy : typeof value == 'string' ? value : undefined;
    if (toCopy) {
      await navigator.clipboard.writeText(toCopy);
      openSnackbar({ message: formatMessage(messages.copySuccessful), severity: 'success' });
    }
  };

  return (
    <MetadataItem
      label={label}
      value={value ?? '-'}
      actionIcon={CopyAllIcon}
      onClick={handleCopy}
      sx={sx}
    />
  );
};

export const MetadataItemEditable: FC<MetadataItemEditableProps> = ({
  label,
  value,
  onSave,
  customValidate,
  customValidateMessage,
  validate,
  optional,
  sx,
}) => {
  const form = useForm<{ newValue: string }>({
    defaultValues: {
      newValue: value ?? '',
    },
    mode: 'all',
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleEdit = async () => {
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      const newValue = form.getValues().newValue;
      await onSave(newValue);
      form.reset({ newValue });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <ListItem>
        <form onSubmit={save} style={{ width: '100%' }}>
          <FormProvider {...form}>
            <FormTextField
              form={form}
              name="newValue"
              label={label}
              helperTextSx={{ width: '220px' }}
              optional={optional ? true : undefined}
              customValidate={customValidate}
              customValidateMessage={customValidateMessage}
              customValidateFn={validate}
            />
            <FormSubmitRow
              submitText="Save"
              onCancel={() => setEditing(false)}
              cancelAlwaysEnabled
              loading={saving}
            />
          </FormProvider>
        </form>
      </ListItem>
    );
  }

  return (
    <MetadataItem
      label={label}
      value={value ?? '-'}
      postText={
        <IconButton onClick={handleEdit}>
          <Edit />
        </IconButton>
      }
      sx={sx}
    />
  );
};

export const EditableText: FC<EditableTextProps> = ({
  label,
  value,
  onSave,
  maxLength,
  children,
}) => {
  const form = useForm<{ newValue: string }>({
    defaultValues: {
      newValue: value,
    },
    mode: 'all',
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleEdit = async () => {
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      const newValue = form.getValues().newValue;

      await onSave(newValue);
      form.reset({ newValue });

      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  if (editing) {
    return (
      <form onSubmit={save}>
        <FormProvider {...form}>
          <Stack direction="row">
            <FormSubmitRow
              submitText="Save"
              onCancel={cancelEditing}
              cancelAlwaysEnabled
              showLoadingAtBottom
              loading={saving}
            >
              <FormTextField
                form={form}
                name="newValue"
                label={label}
                maxLength={maxLength}
                sx={{ width: 650 }}
              />
            </FormSubmitRow>
          </Stack>
        </FormProvider>
      </form>
    );
  }

  return <Box onDoubleClick={handleEdit}>{children}</Box>;
};

const MetadataItemId: FC<{ id: string }> = ({ id }) => {
  const { formatMessage } = useIntl();
  return <MetadataItemCopy label={formatMessage(messages.id)} value={id} />;
};

const MetadataItemCreated: FC<{ createdAt?: string }> = ({ createdAt }) => {
  const { formatMessage } = useIntl();
  return (
    <MetadataItem
      label={formatMessage(messages.created)}
      value={
        createdAt
          ? `${format(new Date(createdAt), 'yyyy-MM-dd hh:mm')} ${getTZ(new Date(createdAt))}`
          : formatMessage(singleMessages.unknown)
      }
    />
  );
};

export const MetadataItemDate: FC<{ value?: string; label: string }> = ({ value, label }) => {
  const { formatMessage } = useIntl();
  let date: Date;

  const formattedValue =
    value && (date = new Date(value))
      ? `${format(date, 'yyyy-MM-dd hh:mm a')} ${getTZ(date)}`
      : formatMessage(singleMessages.unknown);

  return <MetadataItem label={label} value={formattedValue} />;
};

const MetadataItemCreatedBy: FC<{ name?: string }> = ({ name }) => {
  const { formatMessage } = useIntl();
  return (
    <MetadataItem
      label={formatMessage(messages.createdBy)}
      value={name ? name : formatMessage(singleMessages.unknown)}
    />
  );
};

const MetadataItemLastModified: FC<{ updatedAt?: string }> = ({ updatedAt }) => {
  const { formatMessage } = useIntl();
  return (
    <MetadataItem
      label={formatMessage(messages.updatedAt)}
      value={
        updatedAt
          ? `${format(new Date(updatedAt), 'yyyy-MM-dd hh:mm')} ${getTZ(new Date(updatedAt))}`
          : formatMessage(singleMessages.unknown)
      }
    />
  );
};

MetadataItem.Copy = MetadataItemCopy;
MetadataItem.Id = MetadataItemId;
MetadataItem.Created = MetadataItemCreated;
MetadataItem.CreatedBy = MetadataItemCreatedBy;
MetadataItem.Modified = MetadataItemLastModified;

export { MetadataItem };
